<template>
  <div class="smart-search-wrapper">
    <v-autocomplete
      v-if="showSuggestions"
      ref="searchCmp"
      v-model="model"
      :items="productos"
      :loading="isLoading"
      :search-input.sync="inputSearch"
      :color="esPedidoYa ? '#E21247' : 'primary'"
      class="smart-search-input"
      flat
      solo
      hide-details
      autofocus
      :label="label"
      item-text="descripcion"
      return-object
      hide-no-data
      :menu-props="{ offsetY: true, maxHeight: 350, contentClass: 'smart-menu-results' }"
      @keyup.enter="seleccionarProducto"
      @focus.native="$event.target.select()"
    >
      <template v-slot:prepend-inner>
        <v-icon :color="esPedidoYa ? '#E21247' : 'grey darken-1'" class="ml-2">
          {{ currentIcon }}
        </v-icon>
      </template>

      <template v-slot:append>
        <v-fade-transition>
          <div v-if="detectedMode" class="mode-indicator mr-2">
            <span :class="['mode-text', detectedMode.color]">
              {{ detectedMode.text }}
            </span>
          </div>
        </v-fade-transition>
      </template>

      <template v-slot:item="{ item }">
        <v-list-item-content>
          <v-list-item-title class="font-weight-bold d-flex justify-space-between">
            {{ item.descripcion }}
            <span class="primary--text ml-2">$ {{ item.precio_venta }}</span>
          </v-list-item-title>
          <v-list-item-subtitle class="caption grey--text">
            Cod: {{ item.codigo || 'N/A' }} 
            <span v-if="item.codigo_barra" class="ml-2">| <v-icon x-small>mdi-barcode</v-icon> {{ item.codigo_barra }}</span>
          </v-list-item-subtitle>
        </v-list-item-content>
      </template>
    </v-autocomplete>

    <v-text-field
      v-else
      ref="searchCmpTextField"
      v-model="inputSearch"
      :loading="isLoading"
      :color="esPedidoYa ? '#E21247' : 'primary'"
      class="smart-search-input"
      flat
      solo
      hide-details
      autofocus
      :label="label"
      @keyup.enter="seleccionarProducto"
      @focus.native="$event.target.select()"
    >
      <template v-slot:prepend-inner>
        <v-icon :color="esPedidoYa ? '#E21247' : 'grey darken-1'" class="ml-2">
          {{ currentIcon }}
        </v-icon>
      </template>

      <template v-slot:append>
        <v-fade-transition>
          <div v-if="detectedMode" class="mode-indicator mr-2">
            <span :class="['mode-text', detectedMode.color]">
              {{ detectedMode.text }}
            </span>
          </div>
        </v-fade-transition>
      </template>
    </v-text-field>
  </div>
</template>

<script>
import { mapState } from "vuex";
import ProductosAPI from "@/api/productos.js";

export default {
  name: "ProductoBuscadorSmart",
  props: {
    showSuggestions: {
      type: Boolean,
      default: true
    },
    label: {
      type: String,
      default: "Buscar por nombre, código o escáner..."
    },
    endpoint: {
      type: String,
      default: "/productos/p"
    },
    // Opt-in: resuelve un código completo (barcode o código con guión)
    // contra /psp/buscar-por-codigo (match exacto) en vez del filtro de
    // lista (icontains). Solo lo activan los sitios que ya migraron a PSP.
    buscarExacto: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoading: false,
      productos: [],
      inputSearch: "",
      hayProductos: false,
      model: null,
      detectedKey: 'nombre'
    };
  },
  computed: {
    ...mapState("remitos", ["esPedidoYa"]),
    
    currentIcon() {
      if (this.detectedKey === 'barcode') return 'mdi-barcode-scan';
      if (this.detectedKey === 'codigo') return 'mdi-tag-outline';
      return 'mdi-magnify';
    },

    detectedMode() {
      if (!this.inputSearch || this.inputSearch.length < 2) return null;
      if (this.detectedKey === 'barcode') return { text: 'BARCODE', color: 'red--text' };
      if (this.detectedKey === 'codigo') return { text: 'INTERNO', color: 'blue--text' };
      return null;
    }
  },
  methods: {
    focus() {
      const target = this.showSuggestions ? this.$refs.searchCmp : this.$refs.searchCmpTextField;
      if (target) target.focus();
    },

    clearData() {
      this.inputSearch = "";
      this.productos = [];
      this.model = null;
      this.$emit("clean-buscador");
    },

    seleccionarProducto() {
      // Si hay resultados o estamos en modo escaneo
      this.$emit("seleccionar-producto-local");
      
      this.$nextTick(() => { 
        this.inputSearch = ""; 
        this.model = null;
        this.productos = [];
        this.focus();
      });
    },

    getFromSearch(val) {
      if (!val || val.length < 2) {
        this.productos = [];
        return;
      }
      
      this.$emit("loading-buscar-producto", true);

      // Lógica de detección inteligente
      if (/^\d{8,14}$/.test(val)) {
        this.detectedKey = 'barcode';
      } else if (val.includes("-") && /[0-9]/.test(val)) {
        this.detectedKey = 'codigo';
      } else {
        this.detectedKey = 'q';
      }

      this.fetchProductos(this.detectedKey, val);
    },

    fetchProductos(key, val) {
      this.isLoading = true;

      const esCodigoCompleto = key === 'barcode' || key === 'codigo';
      if (this.buscarExacto && esCodigoCompleto) {
        this.fetchProductoPorCodigoExacto(val);
        return;
      }

      const py = this.esPedidoYa ? '&py=true' : '';
      const separator = this.endpoint.includes('?') ? '&' : '?';
      const url = `${this.endpoint}${separator}${key}=${val}${py}&page=1&page_size=12`;

      this.axios.get(url)
        .then((request) => {
          const results = request.data.results.map(p => this.mapearProducto(p));
          this.productos = results;
          this.hayProductos = results.length > 0;

          this.$emit("loading-buscar-producto", false);
          this.$emit("resultado-buscar-producto", results);

          // Si es barcode único, auto-agregar
          if (key === 'barcode' && results.length === 1) {
             this.seleccionarProducto();
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    mapearProducto(p) {
      return {
        ...p,
        precioVenta: p.precio_venta ?? p.precioVenta,
        precioCosto: p.precio_costo ?? p.precioCosto,
        precioBase: p.precio_base ?? p.precioBase,
        cantidadstock: p.cantidad_stock ?? p.cantidadstock,
        controlaStock: p.controla_stock ?? p.controlaStock,
      };
    },

    // Match exacto: a diferencia del filtro de lista, devuelve como mucho un
    // único producto (o 404/409), así que no hace falta desambiguar acá.
    fetchProductoPorCodigoExacto(codigo) {
      ProductosAPI.buscarPspPorCodigo(this.endpoint, codigo)
        .then((response) => {
          const producto = this.mapearProducto(response.data);
          this.productos = [producto];
          this.hayProductos = true;

          this.$emit("loading-buscar-producto", false);
          this.$emit("resultado-buscar-producto", [producto]);

          this.seleccionarProducto();
        })
        .catch((e) => {
          const status = e.response && e.response.status;
          this.productos = [];
          this.hayProductos = false;
          this.$emit("loading-buscar-producto", false);
          this.$emit("resultado-buscar-producto", []);

          if (status === 409) {
            this.$updateBus.$emit("showSnackBar", {
              show: true,
              button: false,
              right: false,
              top: false,
              color: "warning",
              text: "Código de barras duplicado, contactá soporte",
              icon: "mdi-alert",
              timeout: 4000,
            });
          }
          // 404: sin match, se deja en silencio (mismo UX que hoy sin resultados).
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  },
  watch: {
    inputSearch(val) {
      if (!val) {
        this.detectedKey = 'nombre';
        this.productos = [];
        return this.$emit("loading-buscar-producto", false);
      }
      this.getFromSearch(val);
    },
    model(val) {
      if (val && this.showSuggestions) {
        // Al seleccionar de la lista, forzamos que ese sea el resultado
        this.$emit("resultado-buscar-producto", [val]);
        this.seleccionarProducto();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.smart-search-wrapper {
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05) !important;
  border: 1px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.25s ease;
  
  &:focus-within {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1) !important;
    border-color: rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }
}

.smart-search-input {
  ::v-deep .v-input__slot {
    background: transparent !important;
    padding: 0 16px !important;
    height: 54px;
  }
  ::v-deep input {
    font-size: 1.05rem;
    font-weight: 500;
  }
}

.mode-indicator {
  background: rgba(128, 128, 128, 0.1);
  padding: 3px 10px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.mode-text { 
  font-size: 10px; 
  font-weight: 900; 
  letter-spacing: 0.8px;
  font-family: 'Roboto', sans-serif;
}

.red--text { color: #E21247 !important; }
.blue--text { color: #2196F3 !important; }

/* Estilos para el menú de resultados */
::v-deep .smart-menu-results {
  border-radius: 0 0 12px 12px !important;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1) !important;
}

/* Dark Mode support */
.theme--dark {
  .smart-search-wrapper {
    background: #1e1e1e;
    border-color: rgba(255, 255, 255, 0.1);
  }
  .mode-indicator {
    background: rgba(255, 255, 255, 0.05);
  }
}
</style>